import RoundButton from '@basic/RoundButton';
import { DEFAULT_LANDING_PAGE_ACCENT_COLOR } from '@consts';
import { GoalLandingPageFAQModel } from '@models/goals';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { useState } from 'react';
import { usePaymentAndOrderOptional } from '../../payments/PaymentAndOrderContext';
import FAQItem from './FAQItem';
import { parseHighlightedText } from './highlighters';
import { useLandingPageTranslation } from './useLandingPageTranslation';

interface GoalLandingPageFAQSectionProps {
  accentColor?: string;
  faqs?: GoalLandingPageFAQModel[];
}

export default function GoalLandingPageFAQSection({
  accentColor: accentColorProp,
  faqs: faqsProp
}: GoalLandingPageFAQSectionProps = {}) {
  const ctx = usePaymentAndOrderOptional();
  const goal = ctx?.state.goal;
  const accentColor =
    accentColorProp ?? goal?.landingPage?.accentColor ?? DEFAULT_LANDING_PAGE_ACCENT_COLOR;
  const faqs = faqsProp ?? goal?.landingPage?.faqs;
  const [openIndex, setOpenIndex] = useState<number | null>(1);
  const { t } = useLandingPageTranslation();

  if (!faqs) return <></>;

  return (
    <div
      id='faq'
      className={classNames(
        'w-full flex flex-col gap-huge items-center justify-center',
        // RESPONSIVE STYLES
        'p-sm @[640px]:py-xxxl @[768px]:px-none'
      )}
    >
      <div
        className={classNames(
          'flex items-start w-full max-w-[1200px]',
          // RESPONSIVE STYLES
          'flex-col @[768px]:flex-row',
          'gap-xxxl @[768px]:gap-md'
        )}
      >
        <div
          className={classNames(
            setupForDark('bg-landing-surface-card'),
            'flex flex-col rounded-lg',
            // RESPONSIVE STYLES
            'w-full @[768px]:w-auto',
            'gap-huge @[768px]:gap-[100px]',
            'p-xl @[640px]:p-xxxl',
            '@[768px]:max-w-[400px]'
          )}
        >
          <div className={classNames('flex flex-col gap-lg', setupForDark('text-white'))}>
            <div
              className={classNames(
                'flex flex-col font-semibold leading-[1.1]',
                // RESPONSIVE STYLES
                'text-center @[768px]:text-left text-[32px] @[640px]:text-[48px]'
              )}
            >
              {t('domains.goals.landingPage.faq.haveQuestions')}
            </div>
            <div
              className={classNames(
                'flex flex-col font-normal leading-[1.4]',
                // RESPONSIVE STYLES
                'text-center @[768px]:text-left text-[16px] @[640px]:text-[20px]'
              )}
            >
              {parseHighlightedText(
                t('domains.goals.landingPage.faq.checkQuestions', { email: 'hello@imprv.ai' }),
                undefined,
                undefined,
                accentColor
              )}
            </div>
          </div>
          <RoundButton
            type='primary'
            size='large'
            onClick={() => {
              window.location.href = 'mailto:hello@imprv.ai';
            }}
          >
            {t('domains.goals.landingPage.faq.sendEmail')}
          </RoundButton>
        </div>

        <div
          className={classNames(
            'flex-1 flex flex-col',
            'min-w-0',
            // RESPONSIVE STYLES
            'gap-md @[640px]:gap-lg'
          )}
        >
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
              accentColor={accentColor}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
